// A folder in a factory's file tree. Deliberately thin -- a folder is a name and
// a place. Anything that needs to know *about* a file belongs on the file.
export default interface FileFolderModel {
  id: number;
  parentId: number | null;
  name: string;
  depth: number;
  rank: number;
  path: Array<string>;
  fileCount: number;
  children: Array<FileFolderModel>;
}

export const deriveRawToFileFolder = (raw): FileFolderModel => ({
  id: raw.id,
  parentId: raw.parentId ?? null,
  name: raw.name || '',
  depth: Number(raw.depth) || 1,
  rank: Number(raw.rank) || 0,
  path: raw.path || [],
  fileCount: Number(raw.fileCount) || 0,
  children: (raw.children || []).map(deriveRawToFileFolder),
});

/** Depth-first flatten, so a <select> can render the whole tree indented. */
export const flattenFolders = (
  folders: Array<FileFolderModel>,
  depth = 0,
): Array<{ folder: FileFolderModel; depth: number }> =>
  (folders || []).reduce((acc: any[], f) => {
    acc.push({ folder: f, depth });
    return acc.concat(flattenFolders(f.children, depth + 1));
  }, []);

/** The node with this id, wherever it sits in the tree. */
export const findFolder = (
  folders: Array<FileFolderModel>,
  id: number | null,
): FileFolderModel | null => {
  if (!id) return null;
  for (const f of folders || []) {
    if (f.id === id) return f;
    const hit = findFolder(f.children, id);
    if (hit) return hit;
  }
  return null;
};

/** Root-first chain down to this folder, for breadcrumbs. */
export const folderTrail = (
  folders: Array<FileFolderModel>,
  id: number | null,
): Array<FileFolderModel> => {
  if (!id) return [];
  for (const f of folders || []) {
    if (f.id === id) return [f];
    const below = folderTrail(f.children, id);
    if (below.length) return [f, ...below];
  }
  return [];
};
